import WhatsAppHistoryPage from "../features/whatsapp/WhatsAppHistoryPage";
import ConcernsPage from "../features/concerns/ConcernsPage";
import ConcernDetailsPage from "../features/concerns/ConcernDetailsPage";
import WhatsAppMessagePage from "../features/whatsapp/WhatsAppMessagePage";
import UsersPage from "../features/users/UsersPage";
import UserDetailsPage from "../features/users/UserDetailsPage";
import PlatformUsersPage, { RequirePlatformUserAdmin } from "../features/platform-users/PlatformUsersPage";
import CreatePlatformUserPage from "../features/platform-users/CreatePlatformUserPage";
import OrganizationDetailsPage from "../features/organizations/OrganizationDetailsPage";
import { lazy, Suspense, type ReactNode } from "react";
import {
  Users,
  Flag,
  MessageSquare,
  Activity,
  Building2,
  Database,
  FileClock,
  FolderKanban,
  LockKeyhole,
  LogOut,
  ShieldCheck,
  SlidersHorizontal
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import LoginPage from "../features/auth/LoginPage";
import AdminPage, { CreateEntityPage } from "../features/admin/AdminPage";
import AuditLogPage from "../features/audit/AuditLogPage";
import TechOpsDashboardPage from "../features/dashboard/TechOpsDashboardPage";
import MasterDataPage from "../features/master-data/MasterDataPage";
import OrganizationsPage from "../features/organizations/OrganizationsPage";
import ProjectsPage from "../features/projects/ProjectsPage";
import SettingsPage from "../features/settings/SettingsPage";
import { useSession } from "../lib/session";
import { api } from "../lib/api";
import { ListStateProvider } from "../lib/listState";
import { listRouteKeys } from "../lib/listRoutes";

const MonitoringPage = lazy(() => import("../features/monitoring/MonitoringPage"));

function RequireAuth({ children }: { children: ReactNode }) {
  const { accessToken } = useSession();
  const location = useLocation();
  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const { accessToken } = useSession();
  if (accessToken) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function PlatformShell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const dashboardQuery = useQuery({ queryKey: ["tech-ops-dashboard"], queryFn: () => api.getDashboard() });
  const isTechAdmin = dashboardQuery.data?.platform_user.roles.includes("TECH_ADMIN") ?? false;

  function signOut() {
    session.clearSession();
    queryClient.clear();
    navigate("/login", { replace: true });
  }

  return (
    <div className="pa-shell">
      <aside className="pa-sidebar">
        <Logo />
        <div className="pa-product-mark">
          <ShieldCheck size={18} />
          <span>Platform Admin</span>
        </div>
        <nav className="pa-nav" aria-label="Platform admin navigation">
          <NavLink className="pa-nav-item" to="/" end>
            <Activity size={18} />
            <span>Dashboard</span>
          </NavLink>
          <NavLink className="pa-nav-item" to="/users">
            <Users size={18} /><span>Users</span>
          </NavLink>
          <NavLink className="pa-nav-item" to="/monitoring"><Activity size={18} /><span>Monitoring</span></NavLink>
          <NavLink className="pa-nav-item" to="/organizations">
            <Building2 size={18} />
            <span>Organizations</span>
          </NavLink>

          <NavLink className="pa-nav-item" to="/projects">
            <FolderKanban size={18} />
            <span>Projects</span>
          </NavLink>
          <NavLink className="pa-nav-item" to="/master-data">
            <Database size={18} />
            <span>Master Data</span>
          </NavLink>
          <NavLink className="pa-nav-item" to="/whatsapp"><MessageSquare size={18} /><span>WhatsApp messages</span></NavLink>
          <NavLink className="pa-nav-item" to="/concerns"><Flag size={18} /><span>Reported concerns</span></NavLink>
          {isTechAdmin ? (
            <>
              <NavLink className="pa-nav-item" to="/platform-users">
                <ShieldCheck size={18} aria-hidden="true" />
                <span>Platform users</span>
              </NavLink>
              <NavLink className="pa-nav-item" to="/audit-log">
                <FileClock size={18} />
                <span>Audit Log</span>
              </NavLink>
              <NavLink className="pa-nav-item" to="/settings">
                <SlidersHorizontal size={18} />
                <span>Settings</span>
              </NavLink>
              <NavLink className="pa-nav-item" to="/admin">
                <LockKeyhole size={18} />
                <span>Admin</span>
              </NavLink>
            </>
          ) : null}
        </nav>
        <button type="button" className="pa-signout" onClick={signOut}>
          <LogOut size={16} />
          Logout
        </button>
      </aside>
      <main className="pa-main">
        {dashboardQuery.data ? <ListStateProvider key={dashboardQuery.data.platform_user.platform_user_id} scope={dashboardQuery.data.platform_user.platform_user_id} routeKeys={listRouteKeys}>
          <Outlet />
        </ListStateProvider> : dashboardQuery.isError ? <p role="alert">Unable to load your session. <button onClick={() => void dashboardQuery.refetch()}>Retry</button></p> : <p role="status">Loading…</p>}
      </main>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/" element={<RequireAuth><PlatformShell /></RequireAuth>}>
        <Route index element={<TechOpsDashboardPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/new" element={<CreateEntityPage entity="user" />} />
        <Route element={<RequirePlatformUserAdmin />}>
          <Route path="platform-users" element={<PlatformUsersPage />} />
          <Route path="platform-users/new" element={<CreatePlatformUserPage />} />
        </Route>
        <Route path="organizations/new" element={<CreateEntityPage entity="organization" />} />
        <Route path="monitoring" element={<Suspense fallback={<p style={{ padding: 24 }}>Loading monitoring…</p>}><MonitoringPage /></Suspense>} />
        <Route path="users/:userId" element={<UserDetailsPage />} />
        <Route path="organizations/:organizationId" element={<OrganizationDetailsPage />} />
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="connections" element={<Navigate to="/organizations" replace />} />
        <Route path="master-data" element={<MasterDataPage />} />
        <Route path="whatsapp" element={<WhatsAppHistoryPage />} />
        <Route path="concerns" element={<ConcernsPage />} />
        <Route path="concerns/:reportId" element={<ConcernDetailsPage />} />
        <Route path="whatsapp/:messageId" element={<WhatsAppMessagePage />} />
        <Route path="audit-log" element={<AuditLogPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="admin" element={<AdminPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
